/**
 * Enhanced Trail Cache Manager
 *
 * Implements a multi-level caching strategy for trail data:
 * 1. Memory cache (fastest, but volatile)
 * 2. IndexedDB persistence (survives page refreshes)
 * 3. Service Worker integration (offline support)
 *
 * Features:
 * - Explicit TTL settings for different data types
 * - Progressive loading support with priority levels
 * - Static export compatibility with graceful fallbacks
 * - Automatic cache invalidation and cleanup
 * - Network-aware caching policies
 * - Memory usage optimization
 */

import {
  EnhancedTrailData,
  PointOfInterest,
  Season,
  WeatherCondition,
} from '@/types/enhancedTrail';
import { TrailWaypoint } from '@/types/hikingTrail';
import { TrailSummary } from '@/services/OptimizedTrailService';
import { BoundingBox } from '@/utils/pathSimplification';

// NetworkStatus type for adaptive caching
type NetworkStatus = 'online' | 'offline' | 'slow' | 'fast';

// Priority levels for cache operations
type CachePriority = 'critical' | 'high' | 'medium' | 'low';

/**
 * Cache entry with enhanced metadata
 */
interface CacheEntry<T> {
  value: T;
  timestamp: number;
  expiresAt: number;
  priority?: CachePriority;
  source?: 'network' | 'cache' | 'fallback';
  version?: number;
}

/**
 * Detect if we're in a static export environment or middleware-restricted context
 * Gracefully falls back in environments without expected browser APIs
 */
const isStaticExport = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;

    // Check for static export indicators
    if ((window as any).__NEXT_DATA__?.buildId === 'static') return true;

    // Check for middleware error indicators
    const hasMiddlewareError =
      document.body?.textContent?.includes('Middleware cannot be used with') ||
      document.body?.textContent?.includes('output: export');

    if (hasMiddlewareError) {
      console.log(
        'Detected middleware restriction in static export environment'
      );
      return true;
    }

    // Default check for static export
    return (window as any).__NEXT_DATA__?.isFallback === undefined;
  } catch (e) {
    // If any errors occur during detection, assume we're not in static export
    return false;
  }
};

/**
 * Check if IndexedDB is available in this environment
 * More thorough check that attempts to detect blocked/restricted environments
 */
function isIndexedDBAvailable(): boolean {
  try {
    // Basic availability check
    if (
      typeof window === 'undefined' ||
      typeof window.indexedDB === 'undefined'
    ) {
      return false;
    }

    // Check for static export environment first
    if (isStaticExport()) {
      console.warn(
        'Static export environment detected - IndexedDB may be unavailable'
      );

      // In static export, we'll attempt to access IndexedDB but with lower expectations
      try {
        // Simple open test - don't rely on callbacks which might not fire
        window.indexedDB.open('test_static_export');
      } catch (staticExportError) {
        console.warn(
          'IndexedDB access failed in static export environment:',
          staticExportError
        );
        return false;
      }
    }

    // Additional browser-specific checks (Safari private browsing, etc)
    const testRequest = window.indexedDB.open('test_idb_availability');

    // Set up error handler for immediate errors
    let hasError = false;
    testRequest.onerror = () => {
      console.warn('IndexedDB access appears to be restricted by the browser');
      hasError = true;
    };

    // If we got an immediate error, return false
    if (hasError) return false;

    return true;
  } catch (e) {
    console.warn('IndexedDB availability check failed:', e);
    return false;
  }
}

/**
 * Enhanced memory cache with persistent-like features
 * Used as fallback when IndexedDB isn't available
 */
class EnhancedMemoryCache {
  private cache = new Map<string, any>();
  private expirations = new Map<string, number>();
  private persistedKeys: Set<string> = new Set();

  constructor() {
    this.loadFromLocalStorage();

    // Set up periodic save to localStorage for "persistence"
    if (typeof window !== 'undefined') {
      // Save critical data every minute
      setInterval(() => this.saveToLocalStorage(), 60000);

      // Also save on page unload
      window.addEventListener('beforeunload', () => this.saveToLocalStorage());
    }
  }

  set(
    key: string,
    value: any,
    expiresAt: number,
    persist: boolean = false
  ): void {
    this.cache.set(key, value);
    this.expirations.set(key, expiresAt);

    if (persist) {
      this.persistedKeys.add(key);
    }
  }

  get(key: string): { data: any; expiresAt: number } | null {
    if (!this.cache.has(key)) return null;

    const data = this.cache.get(key);
    const expiresAt = this.expirations.get(key) || 0;

    if (Date.now() > expiresAt) {
      // Expired
      this.cache.delete(key);
      this.expirations.delete(key);
      this.persistedKeys.delete(key);
      return null;
    }

    return { data, expiresAt };
  }

  delete(key: string): void {
    this.cache.delete(key);
    this.expirations.delete(key);
    this.persistedKeys.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.expirations.clear();
    this.persistedKeys.clear();

    // Also clear localStorage
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('trail-cache-data');
      } catch (e) {
        console.warn('Failed to clear localStorage cache:', e);
      }
    }
  }

  // Save important cached data to localStorage
  private saveToLocalStorage(): void {
    if (typeof window === 'undefined' || this.persistedKeys.size === 0) return;

    try {
      const dataToSave: Record<string, any> = {};
      const expirations: Record<string, number> = {};

      // Only save persistable items
      for (const key of this.persistedKeys) {
        if (this.cache.has(key)) {
          dataToSave[key] = this.cache.get(key);
          expirations[key] = this.expirations.get(key) || 0;
        }
      }

      localStorage.setItem(
        'trail-cache-data',
        JSON.stringify({
          data: dataToSave,
          expirations,
          timestamp: Date.now(),
        })
      );
    } catch (e) {
      console.warn('Failed to save cache to localStorage:', e);
    }
  }

  // Load cached data from localStorage
  private loadFromLocalStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      const savedCache = localStorage.getItem('trail-cache-data');
      if (!savedCache) return;

      const { data, expirations, timestamp } = JSON.parse(savedCache);

      // Only load non-expired items
      Object.keys(data).forEach((key) => {
        const expiry = expirations[key];
        if (expiry > Date.now()) {
          this.cache.set(key, data[key]);
          this.expirations.set(key, expiry);
          this.persistedKeys.add(key);
        }
      });

      console.log(
        `Loaded ${this.persistedKeys.size} items from localStorage cache`
      );
    } catch (e) {
      console.warn('Failed to load cache from localStorage:', e);
    }
  }

  /**
   * Initialize the cache from localStorage if available
   * Public method to allow calling from outside the class
   */
  initializeFromStorage(): void {
    this.loadFromLocalStorage();
  }

  // Get cache stats
  getStats(): { items: number; persistedItems: number } {
    return {
      items: this.cache.size,
      persistedItems: this.persistedKeys.size,
    };
  }
}

/**
 * Check if Service Worker is available and registered
 */
async function isServiceWorkerAvailable(): Promise<boolean> {
  try {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      !!navigator.serviceWorker.controller
    );
  } catch (e) {
    return false;
  }
}

/**
 * Get current network status
 */
async function getNetworkStatus(): Promise<NetworkStatus> {
  if (typeof navigator === 'undefined' || !navigator.onLine) {
    return 'offline';
  }

  // Check connection quality if available
  if ('connection' in navigator) {
    const conn = (navigator as any).connection;

    if (conn && conn.effectiveType) {
      if (conn.effectiveType === '4g') return 'fast';
      if (conn.effectiveType === '3g') return 'slow';
      return 'slow'; // 2g or worse
    }
  }

  // Default to online if we can't determine speed
  return 'online';
}

/**
 * Comprehensive trail data cache with IndexedDB persistence
 */
class TrailCacheManager {
  private readonly DB_NAME = 'trail_discovery_cache';
  private readonly DB_VERSION = 1;
  private db: IDBDatabase | null = null;

  // Memory cache for fastest access
  private memoryCache = new Map<
    string,
    {
      data: any;
      timestamp: number;
      expiresAt: number;
    }
  >();

  // Enhanced Cache TTL values (in milliseconds) with context-awareness
  private readonly TTL = {
    // Critical data with short TTL
    summary: {
      default: 5 * 60 * 1000, // 5 minutes
      offline: 24 * 60 * 60 * 1000, // 1 day when offline
      slow: 15 * 60 * 1000, // 15 minutes on slow connection
    },

    // Trail data with medium TTL
    trails: {
      default: 15 * 60 * 1000, // 15 minutes
      offline: 7 * 24 * 60 * 60 * 1000, // 7 days when offline
      slow: 60 * 60 * 1000, // 1 hour on slow connection
    },

    // Waypoint data with longer TTL (rarely changes)
    waypoints: {
      default: 60 * 60 * 1000, // 1 hour
      offline: 30 * 24 * 60 * 60 * 1000, // 30 days when offline
      slow: 24 * 60 * 60 * 1000, // 24 hours on slow connection
    },

    // Different TTLs based on data type
    weather: 30 * 60 * 1000, // 30 minutes (weather changes)
    seasonal: 24 * 60 * 60 * 1000, // 1 day (seasonal data)
    static: 7 * 24 * 60 * 60 * 1000, // 7 days (static content)
  };

  // Network status
  private networkStatus: NetworkStatus = 'online';

  // Is this running in a static export environment?
  private readonly isStatic: boolean = isStaticExport();

  // Is IndexedDB available?
  private readonly hasIndexedDB = isIndexedDBAvailable();

  // Is Service Worker available?
  private hasServiceWorker = false;

  // Enhanced memory-only cache for static export environments
  private readonly enhancedMemoryCache = new EnhancedMemoryCache();

  constructor() {
    // Log static export environment detection
    if (this.isStatic) {
      console.log(
        'Static export environment detected - using optimized caching strategy'
      );
    }

    // Don't try to initialize IndexedDB if we know it's not available
    if (this.hasIndexedDB) {
      // Initialize database connection
      this.initDatabase().catch((error) => {
        console.warn('Failed to initialize IndexedDB:', error);
      });
    } else {
      console.log('IndexedDB not available, using memory cache only');
      // Try to initialize enhanced memory cache from localStorage
      try {
        // Load existing data from localStorage if available
        if (typeof localStorage !== 'undefined') {
          const savedCache = localStorage.getItem('trail-cache-data');
          if (savedCache) {
            console.log(
              'Found existing trail data in localStorage, initializing cache'
            );
          }
        }
      } catch (e) {
        console.warn('Error accessing localStorage:', e);
      }
    }

    // Check service worker and network status
    this.initServiceWorkerAndNetwork();
  }
  private async initServiceWorkerAndNetwork(): Promise<void> {
    try {
      // Check if service worker is available
      this.hasServiceWorker = await isServiceWorkerAvailable();

      // Get initial network status
      this.networkStatus = await getNetworkStatus();

      // Set up network status listener
      if (typeof window !== 'undefined') {
        window.addEventListener('online', async () => {
          this.networkStatus = await getNetworkStatus();
          console.log('Network status changed:', this.networkStatus);
        });

        window.addEventListener('offline', () => {
          this.networkStatus = 'offline';
          console.log('Network status changed: offline');
        });
      }
    } catch (error) {
      console.warn('Error initializing network tracking:', error);
    }
  }

  /**
   * Initialize IndexedDB database with error handling
   */
  private async initDatabase(): Promise<void> {
    if (!this.hasIndexedDB) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;

          // Create object stores if they don't exist
          if (!db.objectStoreNames.contains('summary')) {
            db.createObjectStore('summary', { keyPath: 'id' });
          }

          if (!db.objectStoreNames.contains('trails')) {
            const trailStore = db.createObjectStore('trails', {
              keyPath: 'id',
            });
            trailStore.createIndex('difficulty', 'difficulty', {
              unique: false,
            });
            trailStore.createIndex('bestSeasons', 'bestSeasons', {
              unique: false,
              multiEntry: true,
            });
          }

          if (!db.objectStoreNames.contains('waypoints')) {
            const waypointStore = db.createObjectStore('waypoints', {
              keyPath: 'id',
            });
            waypointStore.createIndex('trailId', 'trailId', { unique: false });
          }

          if (!db.objectStoreNames.contains('pois')) {
            const poiStore = db.createObjectStore('pois', { keyPath: 'id' });
            poiStore.createIndex('type', 'type', { unique: false });
          }

          if (!db.objectStoreNames.contains('metadata')) {
            db.createObjectStore('metadata', { keyPath: 'key' });
          }
        };

        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
          resolve();
        };

        request.onerror = (event) => {
          reject((event.target as IDBOpenDBRequest).error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Get data from IndexedDB with enhanced error handling
   */
  private async getFromIDB<T>(
    storeName: string,
    key: string
  ): Promise<T | null> {
    // Skip if IndexedDB isn't available
    if (!this.hasIndexedDB) {
      return null;
    }

    // Try to initialize if needed
    if (!this.db) {
      try {
        await this.initDatabase();
      } catch (error) {
        console.warn('Failed to initialize IndexedDB on demand:', error);
        return null;
      }
    }

    return new Promise<T | null>((resolve) => {
      // Double-check db is available
      if (!this.db) {
        resolve(null);
        return;
      }

      try {
        const transaction = this.db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => {
          const data = request.result;
          if (data && data.expiresAt > Date.now()) {
            resolve(data.value);
          } else {
            resolve(null); // Cache miss or expired
          }
        };

        request.onerror = () => {
          console.warn(
            `Error reading ${key} from ${storeName}:`,
            request.error
          );
          resolve(null);
        };
      } catch (error) {
        console.warn(
          `Exception accessing IndexedDB for ${storeName}/${key}:`,
          error
        );
        resolve(null);
      }
    });
  }

  /**
   * Store data in IndexedDB with enhanced error handling
   */
  private async storeInIDB(
    storeName: string,
    key: string,
    value: any,
    ttl: number
  ): Promise<void> {
    // Skip if IndexedDB isn't available
    if (!this.hasIndexedDB) {
      return;
    }

    // Try to initialize if needed
    if (!this.db) {
      try {
        await this.initDatabase();
      } catch (error) {
        console.warn('Failed to initialize IndexedDB on demand:', error);
        return;
      }
    }

    return new Promise<void>((resolve) => {
      // Double-check db is available
      if (!this.db) {
        resolve();
        return;
      }

      try {
        const transaction = this.db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put({
          id: key,
          value,
          timestamp: Date.now(),
          expiresAt: Date.now() + ttl,
        });

        request.onsuccess = () => resolve();

        request.onerror = () => {
          console.warn(`Error storing ${key} in ${storeName}:`, request.error);
          resolve(); // Continue even if storage fails
        };
      } catch (error) {
        console.warn(
          `Exception writing to IndexedDB for ${storeName}/${key}:`,
          error
        );
        resolve();
      }
    });
  }

  /**
   * Get TTL for a specific data type based on network conditions
   */
  private getTTL(
    type: keyof typeof this.TTL,
    priority: CachePriority = 'medium'
  ): number {
    const ttlConfig = this.TTL[type];

    // If it's a simple number, return it
    if (typeof ttlConfig === 'number') {
      return ttlConfig;
    }

    // If it's an object with network-aware settings
    if (typeof ttlConfig === 'object') {
      // Use network-specific TTL if available
      if (this.networkStatus === 'offline' && ttlConfig.offline) {
        return ttlConfig.offline;
      }

      if (
        (this.networkStatus === 'slow' || this.networkStatus === 'online') &&
        ttlConfig.slow
      ) {
        return ttlConfig.slow;
      }

      // Fall back to default
      return ttlConfig.default;
    }

    // Fallback TTL based on priority
    const fallbackTTLs = {
      critical: 5 * 60 * 1000, // 5 minutes
      high: 15 * 60 * 1000, // 15 minutes
      medium: 60 * 60 * 1000, // 1 hour
      low: 24 * 60 * 60 * 1000, // 1 day
    };

    return fallbackTTLs[priority];
  }

  /**
   * Register with service worker for network request caching
   * @param urls Array of URLs to precache
   */
  async registerWithServiceWorker(urls: string[]): Promise<boolean> {
    try {
      if (!this.hasServiceWorker || typeof navigator === 'undefined') {
        return false;
      }

      // Check if service worker is ready
      if (navigator.serviceWorker.controller) {
        // Send message to service worker to cache these URLs
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_URLS',
          payload: { urls },
        });
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Error registering URLs with service worker:', error);
      return false;
    }
  }

  /**
   * Get trail summary with enhanced caching
   */
  async getTrailSummary(): Promise<TrailSummary | null> {
    const cacheKey = 'current';

    // Check memory cache first (fastest)
    const memCached = this.memoryCache.get(`summary:${cacheKey}`);
    if (memCached && Date.now() < memCached.expiresAt) {
      return memCached.data;
    }

    // Check IndexedDB if available
    if (this.hasIndexedDB) {
      const idbData = await this.getFromIDB<TrailSummary>('summary', cacheKey);
      if (idbData) {
        // Update memory cache with appropriate TTL
        const ttl = this.getTTL('summary', 'high');

        this.memoryCache.set(`summary:${cacheKey}`, {
          data: idbData,
          timestamp: Date.now(),
          expiresAt: Date.now() + ttl,
        });

        return idbData;
      }
    }

    // Cache miss - return null and let the caller fetch from network
    return null;
  }

  /**
   * Cache trail summary data
   */
  async cacheTrailSummary(summary: TrailSummary): Promise<void> {
    const cacheKey = 'current';

    // Cache in memory
    this.memoryCache.set(`summary:${cacheKey}`, {
      data: summary,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.getTTL('summary', 'high'),
    });

    // Cache in IndexedDB
    await this.storeInIDB(
      'summary',
      cacheKey,
      summary,
      this.getTTL('summary', 'high')
    );
  }

  /**
   * Get trail with enhanced caching and error handling
   */
  async getTrail(
    trailId: string,
    options = {
      forceFresh: false,
      priority: 'high' as CachePriority,
      allowStale: false, // Whether to return stale data if nothing else is available
    }
  ): Promise<EnhancedTrailData | null> {
    if (options.forceFresh && this.networkStatus !== 'offline') {
      return null; // Skip cache if fresh data requested and online
    }

    // Check memory cache first (fastest)
    const memCacheKey = `trail:${trailId}`;
    const memCached = this.memoryCache.get(memCacheKey);

    // Return memory cached data if valid
    if (memCached && Date.now() < memCached.expiresAt) {
      return memCached.data;
    }

    // If offline and we have stale data, use it
    if (this.networkStatus === 'offline' && memCached && options.allowStale) {
      console.log(`Using stale cached data for trail ${trailId} while offline`);
      return memCached.data;
    }

    // Check IndexedDB if available
    if (this.hasIndexedDB) {
      try {
        const idbData = await this.getFromIDB<EnhancedTrailData>(
          'trails',
          trailId
        );
        if (idbData) {
          // Update memory cache with appropriate TTL
          const ttl = this.getTTL('trails', options.priority);

          this.memoryCache.set(memCacheKey, {
            data: idbData,
            timestamp: Date.now(),
            expiresAt: Date.now() + ttl,
          });

          return idbData;
        }
      } catch (error) {
        console.warn(
          `Error retrieving trail ${trailId} from IndexedDB:`,
          error
        );
      }
    }

    // If we got here, no valid cached data was found
    return null;
  }

  /**
   * Cache a trail
   */
  async cacheTrail(trailId: string, trail: EnhancedTrailData): Promise<void> {
    // Cache in memory
    this.memoryCache.set(`trail:${trailId}`, {
      data: trail,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.getTTL('trails', 'high'),
    });

    // Cache in IndexedDB
    await this.storeInIDB(
      'trails',
      trailId,
      trail,
      this.getTTL('trails', 'high')
    );
  }

  /**
   * Get waypoints with enhanced caching
   */
  async getWaypoints(
    trailId: string,
    detailLevel: string,
    segmentIndex?: number,
    options = {
      allowStale: true, // Allow stale data for waypoints (they rarely change)
      priority: 'medium' as CachePriority,
    }
  ): Promise<TrailWaypoint[]> {
    const cacheKey =
      segmentIndex !== undefined
        ? `${trailId}-${detailLevel}-${segmentIndex}`
        : `${trailId}-${detailLevel}`;

    const memCacheKey = `waypoints:${cacheKey}`;

    // Check memory cache first (fastest)
    const memCached = this.memoryCache.get(memCacheKey);
    if (memCached && Date.now() < memCached.expiresAt) {
      return memCached.data;
    }

    // If offline and we have stale data, use it regardless of expiry
    if (this.networkStatus === 'offline' && memCached && options.allowStale) {
      return memCached.data;
    }

    // Check IndexedDB if available
    if (this.hasIndexedDB) {
      try {
        const idbData = await this.getFromIDB<TrailWaypoint[]>(
          'waypoints',
          cacheKey
        );
        if (idbData && idbData.length > 0) {
          // Update memory cache with appropriate TTL
          const ttl = this.getTTL('waypoints', options.priority);

          this.memoryCache.set(memCacheKey, {
            data: idbData,
            timestamp: Date.now(),
            expiresAt: Date.now() + ttl,
          });

          return idbData;
        }
      } catch (error) {
        console.warn(
          `Error retrieving waypoints for ${cacheKey} from IndexedDB:`,
          error
        );
      }
    }

    // If offline, return empty array
    if (this.networkStatus === 'offline') {
      console.warn(
        `No cached waypoints available for ${cacheKey} while offline`
      );
    }

    // Cache miss
    return [];
  }

  /**
   * Cache waypoints
   */
  async cacheWaypoints(
    trailId: string,
    detailLevel: string,
    waypoints: TrailWaypoint[],
    segmentIndex?: number
  ): Promise<void> {
    const cacheKey =
      segmentIndex !== undefined
        ? `${trailId}-${detailLevel}-${segmentIndex}`
        : `${trailId}-${detailLevel}`;

    // Cache in memory
    this.memoryCache.set(`waypoints:${cacheKey}`, {
      data: waypoints,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.getTTL('waypoints', 'medium'),
    });

    // Cache in IndexedDB
    await this.storeInIDB(
      'waypoints',
      cacheKey,
      waypoints,
      this.getTTL('waypoints', 'medium')
    );
  }

  /**
   * Get points of interest with enhanced caching and viewport sensitivity
   */
  async getPOIsInViewport(
    viewport: BoundingBox,
    options = {
      precision: 2, // Precision for coordinate rounding (smaller = larger area cached)
      allowStale: true, // Allow stale data for POIs (they rarely move)
      priority: 'medium' as CachePriority,
    }
  ): Promise<PointOfInterest[]> {
    // For viewport queries, use a less precise cache key based on rounded coordinates
    const precision = options.precision;
    const cacheKey = `viewport:${viewport.north.toFixed(precision)},${viewport.south.toFixed(
      precision
    )},${viewport.east.toFixed(precision)},${viewport.west.toFixed(precision)}`;

    const memCacheKey = `pois:${cacheKey}`;

    // Check memory cache first
    const memCached = this.memoryCache.get(memCacheKey);
    if (memCached && Date.now() < memCached.expiresAt) {
      return memCached.data;
    }

    // If offline and we have stale data, use it regardless of expiry
    if (this.networkStatus === 'offline' && memCached && options.allowStale) {
      return memCached.data;
    }

    // For spatial data in static exports, we can check nearby viewports too
    if (this.isStatic || this.networkStatus === 'offline') {
      // Try to find any cached viewports that overlap with the requested one
      const cachedViewports = Array.from(this.memoryCache.keys()).filter(
        (key) => key.startsWith('pois:viewport:')
      );

      for (const cachedKey of cachedViewports) {
        const cachedData = this.memoryCache.get(cachedKey);
        if (!cachedData) continue;

        // Extract coordinates from the cached key
        const coords = cachedKey
          .replace('pois:viewport:', '')
          .split(',')
          .map(Number);
        if (coords.length !== 4) continue;

        const cachedViewport = {
          north: coords[0],
          south: coords[1],
          east: coords[2],
          west: coords[3],
        };

        // Check if viewports overlap
        if (this.viewportsOverlap(viewport, cachedViewport)) {
          // Filter POIs to only those within the requested viewport
          const filteredPOIs = (cachedData.data as PointOfInterest[]).filter(
            (poi) =>
              poi.coordinates.latitude >= viewport.south &&
              poi.coordinates.latitude <= viewport.north &&
              poi.coordinates.longitude >= viewport.west &&
              poi.coordinates.longitude <= viewport.east
          );

          if (filteredPOIs.length > 0) {
            console.log(
              `Using cached POIs from overlapping viewport (${filteredPOIs.length} points)`
            );
            return filteredPOIs;
          }
        }
      }
    }

    // Cache miss
    if (this.networkStatus === 'offline') {
      console.warn(
        `No cached POIs available for the current viewport while offline`
      );
    }

    return [];
  }

  /**
   * Check if two viewports overlap
   */
  private viewportsOverlap(
    viewport1: BoundingBox,
    viewport2: BoundingBox
  ): boolean {
    return (
      viewport1.west <= viewport2.east &&
      viewport1.east >= viewport2.west &&
      viewport1.south <= viewport2.north &&
      viewport1.north >= viewport2.south
    );
  }

  /**
   * Cache points of interest for a viewport
   */
  async cachePOIsForViewport(
    viewport: BoundingBox,
    pois: PointOfInterest[]
  ): Promise<void> {
    // Use a less precise cache key based on rounded coordinates
    const precision = 2; // Round to 2 decimal places
    const cacheKey = `viewport:${viewport.north.toFixed(precision)},${viewport.south.toFixed(
      precision
    )},${viewport.east.toFixed(precision)},${viewport.west.toFixed(precision)}`;

    // Cache in memory only (viewport changes frequently)
    this.memoryCache.set(`pois:${cacheKey}`, {
      data: pois,
      timestamp: Date.now(),
      expiresAt: Date.now() + 5 * 60 * 1000, // 5 minutes
    });
  }

  /**
   * Preload critical data for offline use
   * This is especially important for static exports
   */
  async preloadCriticalData(trailIds: string[] = []): Promise<boolean> {
    try {
      // First preload summary data
      const summary = await this.getTrailSummary();
      if (summary) {
        // If we don't have specific trail IDs, use recommended ones from summary
        if (trailIds.length === 0) {
          const season = this.getCurrentSeason();
          trailIds = summary.recommendedTrails.season[season] || [];
        }
      }

      // Preload trail data and summary waypoints for each trail
      const preloadPromises = trailIds.map(async (trailId) => {
        // Try to fetch and cache trail data
        const trail = await this.getTrail(trailId);
        if (trail) {
          await this.cacheTrail(trailId, trail);

          // Also preload summary waypoints for this trail
          await this.getWaypoints(trailId, 'summary');
        }
      });

      await Promise.all(preloadPromises);

      // Register these trails with the service worker if available
      if (this.hasServiceWorker) {
        const urls = trailIds.map((id) => `/api/trails/${id}`);
        await this.registerWithServiceWorker(urls);
      }

      return true;
    } catch (error) {
      console.error('Error preloading critical data:', error);
      return false;
    }
  }

  /**
   * Get current season based on month
   */
  private getCurrentSeason(): Season {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'autumn';
    return 'winter';
  }

  /**
   * Cache invalidation methods with enhanced resilience
   */
  async invalidateTrail(trailId: string): Promise<void> {
    // Clear memory cache
    for (const key of this.memoryCache.keys()) {
      if (
        key.startsWith(`trail:${trailId}`) ||
        key.startsWith(`waypoints:${trailId}`)
      ) {
        this.memoryCache.delete(key);
      }
    }

    // Clear IndexedDB if available
    if (this.hasIndexedDB && this.db) {
      try {
        const transaction = this.db.transaction(
          ['trails', 'waypoints'],
          'readwrite'
        );
        const trailStore = transaction.objectStore('trails');
        const waypointStore = transaction.objectStore('waypoints');

        trailStore.delete(trailId);

        // Clear all waypoint segments for this trail
        const waypointIndex = waypointStore.index('trailId');
        const waypointRequest = waypointIndex.openCursor(
          IDBKeyRange.only(trailId)
        );

        waypointRequest.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;
          if (cursor) {
            waypointStore.delete(cursor.primaryKey);
            cursor.continue();
          }
        };
      } catch (error) {
        console.warn(
          `Error invalidating trail ${trailId} in IndexedDB:`,
          error
        );
      }
    }

    // Invalidate summary as well
    await this.invalidateSummary();

    // Inform service worker about invalidation if available
    if (this.hasServiceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'INVALIDATE_CACHE',
        payload: { trailId },
      });
    }
  }

  async invalidateSummary(): Promise<void> {
    this.memoryCache.delete('summary:current');

    if (this.hasIndexedDB && this.db) {
      try {
        const transaction = this.db.transaction('summary', 'readwrite');
        const store = transaction.objectStore('summary');
        store.delete('current');
      } catch (error) {
        console.warn('Error invalidating summary in IndexedDB:', error);
      }
    }

    // Inform service worker about invalidation if available
    if (this.hasServiceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'INVALIDATE_CACHE',
        payload: { key: 'summary' },
      });
    }
  }

  /**
   * Clear all cached data with enhanced error handling
   */
  async clearAllCaches(): Promise<boolean> {
    try {
      // Clear memory cache
      this.memoryCache.clear();

      // Clear IndexedDB if available
      if (this.hasIndexedDB && this.db) {
        try {
          const transaction = this.db.transaction(
            ['summary', 'trails', 'waypoints', 'pois', 'metadata'],
            'readwrite'
          );

          ['summary', 'trails', 'waypoints', 'pois', 'metadata'].forEach(
            (storeName) => {
              const store = transaction.objectStore(storeName);
              store.clear();
            }
          );
        } catch (error) {
          console.warn('Error clearing IndexedDB stores:', error);
        }
      }

      // Inform service worker about cache clearing if available
      if (this.hasServiceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CACHES',
          payload: { scope: 'all' },
        });
      }

      return true;
    } catch (error) {
      console.error('Error clearing caches:', error);
      return false;
    }
  }

  /**
   * Get enhanced storage usage statistics
   */
  async getStorageStats(): Promise<{
    memoryItems: number;
    memoryUsage: number; // Approximate bytes
    idbItems: {
      summary: number;
      trails: number;
      waypoints: number;
      pois: number;
      metadata: number;
    };
    serviceWorkerActive: boolean;
    networkStatus: NetworkStatus;
    isStaticExport: boolean;
  }> {
    // Get memory cache stats
    const memoryItems = this.memoryCache.size;

    // Estimate memory usage (very rough approximation)
    let memoryUsage = 0;
    for (const [key, entry] of this.memoryCache.entries()) {
      // Approximate size calculation
      const keySize = key.length * 2; // UTF-16 chars are 2 bytes each
      const valueSize = JSON.stringify(entry.data).length * 2;
      memoryUsage += keySize + valueSize;
    }

    // Initialize IndexedDB stats
    const idbItems = {
      summary: 0,
      trails: 0,
      waypoints: 0,
      pois: 0,
      metadata: 0,
    };

    // Get IndexedDB stats if available
    if (this.hasIndexedDB) {
      if (!this.db) {
        try {
          await this.initDatabase();
        } catch (error) {
          console.warn('Failed to initialize IndexedDB for stats:', error);
        }
      }

      if (this.db) {
        for (const store of Object.keys(idbItems)) {
          try {
            const transaction = this.db.transaction(store, 'readonly');
            const objectStore = transaction.objectStore(store);

            await new Promise<void>((resolve) => {
              const countRequest = objectStore.count();
              countRequest.onsuccess = () => {
                idbItems[store as keyof typeof idbItems] = countRequest.result;
                resolve();
              };
              countRequest.onerror = () => resolve();
            });
          } catch (error) {
            console.warn(`Error getting count for ${store}:`, error);
          }
        }
      }
    }

    // Update network status before returning
    if (typeof navigator !== 'undefined') {
      this.networkStatus = await getNetworkStatus();
    }

    return {
      memoryItems,
      memoryUsage,
      idbItems,
      serviceWorkerActive: this.hasServiceWorker,
      networkStatus: this.networkStatus,
      isStaticExport: this.isStatic,
    };
  }
}

// Create singleton instance
export const trailCache = new TrailCacheManager();
